import { Card, CardProps as ChakraUICardProps } from "@chakra-ui/react";

import useResizeObserver from "@react-hook/resize-observer";

import {
  ReactElement,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState
} from "react";

import { Header } from "./header/header";
import { Body } from "./body/body";
import { Footer } from "./footer/footer";

export interface CardProps extends ChakraUICardProps {
  id?: string;
  cardVariant?: string;
  username?: string;
  userAvatarSrc?: string;
  contributionTime?: string;
  showPin?: boolean;
  imageSrc?: string;
  imageAlt?: string;
  cardHeading?: string;
  cardDescription?: string | ReactNode;
  showDetailedText?: boolean;
  tileBackgroundColor?: string;
  showDescription?: boolean;
  pinType?: string;
  pinAddress?: string;
  pinIcon?: ReactElement;
  pinImageSrc?: string;
  ariaLabelMapPinIcon?: string;
  ariaLabelVerticalDotsIcon?: string;
  ariaLabelCommentIcon?: string;
  ariaLabelVoteIcon?: string;
  ariaLabelThumbsDownIcon?: string;
  ariaLabelShareIcon?: string;
  commentsCount?: string | number;
  thumbsUpCount?: string | number;
  thumbsDownCount?: string | number;
  shareText?: string;
  showCommentIcon?: boolean;
  isThumbsUp?: boolean;
  isThumbsDown?: boolean;
  isShareIcon?: boolean;
  showTags?: boolean;
  tags?: Array<string>;
  handleMoreOptions?(): any;
  handleComment?(): any;
  handleVoteUp?(): any;
  handleVoteDown?(): any;
  handleShare?(): any;
  showImage?: boolean;
  showFooter?: boolean;
  isLoaded?: boolean;
  showMoreOptions?: boolean;
  hasHeaderHighlight?: boolean;
  hasBodyHighlight?: boolean;
  hasFooterHighlight?: boolean;
  pinColor?: string;
  actionList?: ReactNode;
  handleOnBodyClick?: any;
  isVoteActive?: boolean;
  isVoteDisabled?: boolean;
  isIconButtonPressed?: boolean;
  isAddressVisible?: boolean;
  dataElement?: any;
  makeFooterViewOnly?: boolean;
  magnifiedImage?: boolean;
  isCursor?: boolean;
  socialShareTitle?: string;
  socialShareUrl?: string;
  numberOfLines?: number;
}
interface ResizeObserverData {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
  x: number;
  y: number;
}

export function Contribution(props: CardProps) {
  const {
    id,
    cardVariant,
    username,
    userAvatarSrc,
    contributionTime,
    showPin,
    imageSrc,
    imageAlt,
    cardHeading,
    cardDescription,
    showDetailedText,
    tileBackgroundColor,
    showDescription,
    pinType,
    pinAddress,
    pinColor,
    pinIcon,
    pinImageSrc,
    ariaLabelMapPinIcon,
    ariaLabelVerticalDotsIcon,
    ariaLabelCommentIcon,
    ariaLabelVoteIcon,
    ariaLabelThumbsDownIcon,
    ariaLabelShareIcon,
    commentsCount,
    shareText,
    showCommentIcon,
    isThumbsDown,
    isThumbsUp,
    isShareIcon,
    showTags,
    thumbsDownCount,
    thumbsUpCount,
    tags,
    handleMoreOptions,
    handleComment,
    handleVoteUp,
    handleVoteDown,
    handleShare,
    showImage,
    showFooter,
    isLoaded,
    showMoreOptions,
    hasHeaderHighlight,
    hasBodyHighlight,
    hasFooterHighlight,
    actionList,
    handleOnBodyClick,
    isVoteActive,
    isVoteDisabled,
    isIconButtonPressed,
    isAddressVisible,
    dataElement,
    makeFooterViewOnly,
    magnifiedImage,
    isCursor,
    socialShareTitle,
    socialShareUrl,
    numberOfLines
  } = props;

  const useSize = (target: RefObject<HTMLDivElement>) => {
    const [size, setSize] = useState<ResizeObserverData | null>(null);

    useEffect(() => {
      if (target.current) {
        setSize(target.current.getBoundingClientRect());
      }
    }, [target]);

    // eslint-disable-next-line no-undef
    useResizeObserver(target, (entry: ResizeObserverEntry) => {
      setSize(entry.contentRect);
    });

    return size;
  };

  const target = useRef<HTMLDivElement>(null);
  const size = useSize(target);
  const cardWidth = size?.width ? size?.width : 0;

  return (
    <Card
      ref={target}
      id={id}
      variant={cardVariant}
      gap={4}
      w="full"
      h="full"
      bgColor={cardVariant === "elevated" ? "whiteAlpha.100" : "transparent"}
    >
      <Header
        contributionTime={contributionTime}
        imageSrc={imageSrc}
        userAvatarSrc={userAvatarSrc}
        username={username}
        showPin={showPin}
        cardVariant={cardVariant}
        handleMoreOptions={handleMoreOptions}
        showImage={showImage}
        isLoaded={isLoaded}
        ariaLabelMapPinIcon={ariaLabelMapPinIcon}
        ariaLabelVerticalDotsIcon={ariaLabelVerticalDotsIcon}
        showMoreOptions={showMoreOptions}
        hasHeaderHighlight={hasHeaderHighlight}
        actionList={actionList}
        cardSize={cardWidth}
      />
      <Body
        cardDescription={cardDescription}
        cardHeading={cardHeading}
        imageAlt={imageAlt}
        imageSrc={imageSrc}
        pinType={pinType}
        pinAddress={pinAddress || ""}
        pinIcon={pinIcon}
        isAddressVisible={isAddressVisible}
        pinImageSrc={pinImageSrc}
        cardVariant={cardVariant}
        showImage={showImage}
        isLoaded={isLoaded}
        tileBackgroundColor={tileBackgroundColor}
        showDescription={showDescription}
        showDetailedText={showDetailedText}
        hasBodyHighlight={hasBodyHighlight}
        pinColor={pinColor}
        contributionTime={contributionTime}
        userAvatarSrc={userAvatarSrc}
        username={username}
        handleOnBodyClick={handleOnBodyClick}
        dataElement={dataElement}
        magnifiedImage={magnifiedImage}
        isCursor={isCursor}
        numberOfLines={numberOfLines}
      />
      {showFooter && (
        <Footer
          cardVariant={cardVariant}
          commentsCount={commentsCount}
          showCommentIcon={showCommentIcon}
          isThumbsDown={isThumbsDown}
          isThumbsUp={isThumbsUp}
          shareText={shareText}
          thumbsDownCount={thumbsDownCount}
          thumbsUpCount={thumbsUpCount}
          ariaLabelCommentIcon={ariaLabelCommentIcon}
          ariaLabelVoteIcon={ariaLabelVoteIcon}
          ariaLabelThumbsDownIcon={ariaLabelThumbsDownIcon}
          ariaLabelShareIcon={ariaLabelShareIcon}
          tags={tags}
          showTags={showTags}
          handleComment={handleComment}
          handleVoteUp={handleVoteUp}
          handleVoteDown={handleVoteDown}
          handleShare={handleShare}
          isLoaded={isLoaded}
          isShareIcon={isShareIcon}
          hasFooterHighlight={hasFooterHighlight}
          isVoteActive={isVoteActive}
          isVoteDisabled={isVoteDisabled}
          isIconButtonPressed={isIconButtonPressed}
          makeFooterViewOnly={makeFooterViewOnly}
          socialShareTitle={socialShareTitle}
          socialShareUrl={socialShareUrl}
        />
      )}
    </Card>
  );
}
